"use client"

import { useState, useEffect } from "react"
import {
  Search,
  Filter,
  Eye,
  Clock,
  CheckCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  X,
} from "lucide-react"
import { get } from "../utility/fetch"

export default function MyFeedback() {
  const [searchTerm, setSearchTerm] = useState("")
  const [filterStatus, setFilterStatus] = useState("all")
  const [feedbacks, setFeedbacks] = useState([])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(false)

  const [modalFeedback, setModalFeedback] = useState(null)
  const [modalLoading, setModalLoading] = useState(false)

  const fetchFeedbacks = async () => {
    try {
      setLoading(true)
      const res = await get(`/api/customerengagement/list/${page}/${pageSize}`)
      if (res.data) {
        setFeedbacks(res.data.recordList || [])
        setTotalPages(res.data.metadata?.totalPages || 1)
      }
    } catch (err) {
      console.error("Error fetching feedbacks", err)
    } finally {
      setLoading(false)
    }
  }

  const fetchFeedbackDetails = async (id) => {
    try {
      setModalLoading(true)
      setModalFeedback(null)
      const res = await get(`/api/customerengagement/${id}`)
      if (res.data) {
        setModalFeedback(res.data)
      }
    } catch (err) {
      console.error("Error fetching feedback details", err)
    } finally {
      setModalLoading(false)
    }
  }

  useEffect(() => {
    fetchFeedbacks()
  }, [page, pageSize])

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case "pending":
        return <Clock className="w-4 h-4" />
      case "in-progress":
        return <AlertCircle className="w-4 h-4" />
      case "resolved":
        return <CheckCircle className="w-4 h-4" />
      default:
        return <Clock className="w-4 h-4" />
    }
  }

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "pending":
        return "bg-yellow-100 text-yellow-800"
      case "in-progress":
        return "bg-blue-100 text-blue-800"
      case "resolved":
        return "bg-green-100 text-green-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })

  const filteredFeedback = feedbacks.filter((f) => {
    const matchSearch =
      f.trackingId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.message?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchStatus = filterStatus === "all" || f.status?.toLowerCase() === filterStatus
    return matchSearch && matchStatus
  })

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Customer Engagement</h1>
        <p className="text-gray-600 mt-1">Track feedback</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search feedback..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="in-progress">In Progress</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>

            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value))
                setPage(1)
              }}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {[5, 10, 20, 50].map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Feedback Table */}
      <div className="overflow-x-auto bg-white border border-gray-200 rounded-xl shadow-sm">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="animate-spin w-6 h-6 text-primary" />
            <span className="ml-2 text-sm text-gray-600">Loading feedback...</span>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left font-medium text-gray-500">Tracking ID</th>
                <th className="px-6 py-3 text-left font-medium text-gray-500">Type</th>
                <th className="px-6 py-3 text-left font-medium text-gray-500">Status</th>
                <th className="px-6 py-3 text-left font-medium text-gray-500">Submitted</th>
                <th className="px-6 py-3 text-left font-medium text-gray-500">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredFeedback.map((f) => (
                <tr key={f.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-gray-900">{f.trackingId}</td>
                  <td className="px-6 py-4">{f.type}</td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                        f.status
                      )}`}
                    >
                      {getStatusIcon(f.status)}
                      <span className="ml-1 capitalize">{f.status}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4">{formatDate(f.createdAt)}</td>
                  <td className="px-6 py-4">
                    <button
                      className="text-blue-600 hover:underline flex items-center gap-1"
                      onClick={() => {
                        setModalLoading(true)
                        setModalFeedback(null)
                        fetchFeedbackDetails(f.id)
                      }}

                    >
                      <Eye className="w-4 h-4" />
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {!loading && filteredFeedback.length === 0 && (
          <div className="text-center py-8 text-gray-600">No feedback found.</div>
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <button
          className="flex items-center px-4 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          disabled={page <= 1}
          onClick={() => setPage((prev) => prev - 1)}
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Previous
        </button>

        <span className="text-sm text-gray-700">
          Page {page} of {totalPages}
        </span>

        <button
          className="flex items-center px-4 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          disabled={page >= totalPages}
          onClick={() => setPage((prev) => prev + 1)}
        >
          Next
          <ChevronRight className="w-4 h-4 ml-1" />
        </button>
      </div>

      {/* Feedback Details Modal */}

      {modalFeedback !== null || modalLoading ? (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center px-4">
          <div className="bg-white w-full max-w-lg p-6 rounded-xl shadow-2xl relative">
            {/* Close Button */}
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              onClick={() => {
                setModalFeedback(null)
                setModalLoading(false)
              }}
            >
              <X className="w-5 h-5" />
            </button>

            {/* Modal Content */}
            {modalLoading ? (
              <div className="flex flex-col items-center justify-center py-10">
                <Loader2 className="w-8 h-8 text-primary animate-spin mb-3" />
                <p className="text-gray-600">Loading feedback details...</p>
              </div>
            ) : (
              <div className="space-y-4">
                <h2 className="text-2xl font-semibold text-gray-800 mb-2">Feedback Details</h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 text-sm text-gray-700">
                  <div>
                    <span className="font-medium text-gray-500">Tracking ID:</span>
                    <p>{modalFeedback.trackingId}</p>
                  </div>

                  <div>
                    <span className="font-medium text-gray-500">Type:</span>
                    <p>{modalFeedback.type}</p>
                  </div>

                  <div>
                    <span className="font-medium text-gray-500">Status:</span>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                        modalFeedback.status
                      )}`}
                    >
                      {getStatusIcon(modalFeedback.status)}
                      <span className="ml-1 capitalize">{modalFeedback.status}</span>
                    </span>
                  </div>

                  <div>
                    <span className="font-medium text-gray-500">Location:</span>
                    <p>{modalFeedback.location || "—"}</p>
                  </div>

                  <div>
                    <span className="font-medium text-gray-500">Email:</span>
                    <p>{modalFeedback.contactEmail}</p>
                  </div>

                  <div>
                    <span className="font-medium text-gray-500">Submitted On:</span>
                    <p>{formatDate(modalFeedback.createdAt)}</p>
                  </div>

                  <div className="sm:col-span-2">
                    <span className="font-medium text-gray-500">Message:</span>
                    <p className="whitespace-pre-wrap">{modalFeedback.message}</p>
                  </div>

                  {modalFeedback.actionTaken && (
                    <div className="sm:col-span-2">
                      <span className="font-medium text-gray-500">Action Taken:</span>
                      <p>{modalFeedback.actionTaken}</p>
                    </div>
                  )}

                  {modalFeedback.resolutionNotes && (
                    <div className="sm:col-span-2">
                      <span className="font-medium text-gray-500">Resolution Notes:</span>
                      <p>{modalFeedback.resolutionNotes}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}

    </div>
  )
}
